// Sorting a list that is loaded whole — a handful of branches, the mailboxes, the
// kenmerken — in the browser, the way the paged lists sort on the server
// (docs/api.md §9.18): text as people read it (HAP 2 before HAP 10, case and
// accents aside), true before false so "active" comes first, and nothing at all
// last in either direction. Rows that tie keep the order they came in.

export type SortValue = string | number | boolean | null | undefined;

const TEXT = new Intl.Collator("nl", { numeric: true, sensitivity: "base" });

function isEmpty(value: SortValue): value is null | undefined | "" {
  return value === null || value === undefined || value === "";
}

function compareValues(a: Exclude<SortValue, null | undefined>, b: Exclude<SortValue, null | undefined>): number {
  if (typeof a === "boolean" || typeof b === "boolean") return Number(b === true) - Number(a === true);
  if (typeof a === "number" && typeof b === "number") return a - b;
  return TEXT.compare(String(a), String(b));
}

/**
 * The rows in the order of `sort`, using `values` to read what a row is sorted
 * on. A sort key without a reader leaves the rows as they are.
 */
export function sortRows<T>(
  rows: readonly T[],
  sort: { sort: string; order: "asc" | "desc" },
  values: Readonly<Record<string, (row: T) => SortValue>>,
): T[] {
  const read = values[sort.sort];
  if (!read) return [...rows];
  const direction = sort.order === "desc" ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, value: read(row) }))
    .sort((a, b) => {
      const aEmpty = isEmpty(a.value);
      const bEmpty = isEmpty(b.value);
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.index - b.index : aEmpty ? 1 : -1;
      return compareValues(a.value!, b.value!) * direction || a.index - b.index;
    })
    .map((entry) => entry.row);
}
